import React, { useCallback, useMemo, useRef } from 'react';
import toastr from 'toastr';
import { Form, Icon, Table } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { upsertComment } from '../../../../actions/commentActions';

import Joyn2Button from '../../../common/Joyn2Button';
import ApiService from '../../../../service/apiService';
import { STATUS_LIST } from '../../../../HelperMethods/enums';
import { determineIfUserHasFieldsDisabled } from '../../../../HelperMethods/UserUtilities';
import { isTaskChild } from '../../../../HelperMethods/TaskUtilities';
import { ACCESS_OPTIONS, ENTITY_ACCESS_TYPE, RESOURCES } from '../../../../HelperMethods/role-resource-constants';
import { sleep } from '../../../../HelperMethods/UtilityFunctions';
import { convertUTCDateStringToYYYYMMDDHHMMSSFormat } from '../../../../HelperMethods/DateUtils';

import CustomDropdown from '../../../common/CustomDropdown';
import { ReactComponent as IButton } from '../../../../icons/iButton.svg';
import { ReactComponent as IButtonDisabled } from '../../../../icons/iButtonDisabled.svg';
// eslint-disable-next-line import/no-cycle
import { acceptRejectTask } from './redux/actions';
import { hasAssigneeAccepted, shouldAllowAcceptOrReject, isUserAddedOrInvalid } from './lib/helper';
import QRCodePopup from '../../../common/QRCodePopup';

const addComments = ({ dispatch, reqAssignee, loggedInUser, task, type = 'accept', selectedField }) => {
  dispatch(
    upsertComment({
      comment: `${type === 'accept' ? 'Accepted' : 'Rejected'} Task for ${reqAssignee.text} (${
        reqAssignee.originalobject.userName
      }) by ${loggedInUser.firstName || ''} ${loggedInUser.lastName || ''} (${loggedInUser.userName})`,
      fieldId: _.get(task, 'fieldId') || selectedField.id,
      taskId: task.id,
      autoGenerated: true,
    })
  );
};

const filterUsedNames = (formik, assigneeOptions, id) => {
  const ids = _.get(formik, 'values.s_assigneeid', []);
  const options = assigneeOptions.items.filter((item) => {
    if (item.value === id) {
      return true;
    }
    if (ids.includes(item.value)) {
      return false;
    }
    return true;
  });
  return { items: options, isLoading: assigneeOptions.isLoading };
};

const AssigneeComponent = ({ taskTypeOptions, assigneeOptions, formik, task, loggedInUser, ...props }) => {
  const {
    onValueChange: { viewAssigneeDetals, onAssigneeChange },
    onCreateButtonClicked,
    onUpdate,
    validateOverallTaskForm,
    taskType: { needEquipment = false } = {},
  } = props;
  const history = useHistory();
  const qrModalRef = useRef();
  const selectedField = useSelector((state) => state.appReducer.selectedField);
  const dispatch = useDispatch();
  const selectedTaskType = taskTypeOptions.items.find((option) => option.value === _.get(formik, 'values.s_tasktype'));
  const selectedttOriginalObject = _.get(selectedTaskType, 'originalobject', {}) || {};

  const assigneeIds = !_.size(_.get(formik, 'values.s_assigneeid'))
    ? ['']
    : _.get(formik, 'values.s_assigneeid') || [''];

  let equipmentOptions = [];
  let operatorOptions = [];
  let operatorId = '';
  let equipmentId = '';
  if (needEquipment) {
    equipmentOptions = _.get(assigneeOptions, 'items', []).filter((a) => a.originalobject.isEquipmentResource);
    operatorOptions = _.get(assigneeOptions, 'items', []).filter((a) => !a.originalobject.isEquipmentResource);
    if (!equipmentId) {
      const equipmentOptionsDict = _.keyBy(equipmentOptions, 'value');
      equipmentId = assigneeIds.find((id) => equipmentOptionsDict[id]) || '';
      if (equipmentId) {
        const equipmentVendorId = equipmentOptionsDict[equipmentId]?.originalobject?.vendorId;
        if (equipmentVendorId) {
          operatorOptions = operatorOptions.filter((val) => val.originalobject.vendorId === equipmentVendorId);
        }
      }
    }
    if (!operatorId) {
      const operatorOptionsDict = _.keyBy(operatorOptions, 'value');
      operatorId = assigneeIds.find((id) => operatorOptionsDict[id]) || '';
    }
  }
  const handleFieldChange = (data, index) => {
    const { value, options } = data;
    const assigneeIdsClone = _.cloneDeep(_.get(formik, 'values.s_assigneeid', []));
    // [equipment, operator];
    if (needEquipment) {
      if (index === 0) {
        assigneeIdsClone[0] = value;
        const equipmentOption = options.find((option) => option.value === value);
        assigneeIdsClone[1] = equipmentOption?.originalobject?.operatorId || ''; // to be prefilled
      } else {
        // const equipmentIdIndex = assigneeIdsClone.findIndex((id) => id === equipmentId);
        const operatorIdIndex = assigneeIdsClone.findIndex((id) => id === operatorId);
        if (operatorIdIndex > -1) {
          assigneeIdsClone[operatorIdIndex] = value;
        }
      }
    } else {
      assigneeIdsClone[index] = value;
    }
    if (index === assigneeIdsClone.length - 1 && !needEquipment) {
      assigneeIdsClone.push('');
    }
    onAssigneeChange({ name: 's_assigneeid', value: assigneeIdsClone, formik });
    formik.setFieldTouched('s_assigneeid', true, false);
  };

  const handleDeleteRow = (index) => {
    const assigneeIdsClone = _.cloneDeep(_.get(formik, 'values.s_assigneeid', []));
    assigneeIdsClone.splice(index, 1);

    onAssigneeChange({ name: 's_assigneeid', value: assigneeIdsClone, formik });
  };

  const getParam = (assigneeId, operatorIdArg) => {
    const assigneeIdsParam = [assigneeId];
    if (operatorIdArg) {
      assigneeIdsParam.push(operatorIdArg);
    }
    const param = { ...formik.values, s_assigneeid: assigneeIdsParam };

    if (!isTaskChild(task)) {
      param.s_status = 'open';
    }
    return param;
  };

  const acceptTaskForNewAssignee = async ({ assigneeId, operatorIdArg, data, reqAssignee }) => {
    const isValid = await validateOverallTaskForm(formik);
    let updatedTask;
    if (!_.size(formik.errors) && isValid) {
      dispatch(acceptRejectTask(data));
      if (!isTaskChild(task)) {
        formik.setFieldValue('s_status', STATUS_LIST.OPEN.key, false);
      }
      // onAssigneeChange({ name: 's_assigneeid', value: [assigneeId], formik });
      // change in reducer and formik values takes time so sleep is added
      await sleep(1000);
      if (task.id) {
        updatedTask = task;
        const taskUpdateParams = getParam(assigneeId, operatorIdArg);
        await onUpdate(taskUpdateParams, formik);
      } else {
        const taskCreateParams = getParam(assigneeId, operatorIdArg);
        updatedTask = await onCreateButtonClicked(taskCreateParams, formik);
      }
      addComments({ dispatch, reqAssignee, loggedInUser, task: updatedTask, selectedField });
      toastr.success('Task Accepted Sucessfully');
      history.goBack();
    }
  };
  const acceptTaskForExistingAssignee = async ({ assigneeId, operatorIdArg, data, reqAssignee }) => {
    dispatch(acceptRejectTask(data));
    if (!isTaskChild(task)) {
      formik.setFieldValue('s_status', STATUS_LIST.OPEN.key, false);
    }
    const assigneeChangedValue = operatorIdArg ? [assigneeId, operatorIdArg] : [assigneeId];
    onAssigneeChange({ name: 's_assigneeid', value: assigneeChangedValue, formik });
    const taskUpdateParams = getParam(assigneeId, operatorIdArg);
    await sleep(1000);
    await onUpdate(taskUpdateParams, formik);
    toastr.success('Task Accepted Sucessfully');
    addComments({ dispatch, reqAssignee, loggedInUser, task });
    history.goBack();
  };

  const acceptTask = async (assigneeId, operatorIdArg) => {
    try {
      const data = { taskId: task.id, assigneeId, operatorId: operatorIdArg };
      const reqAssignee = _.get(assigneeOptions, 'items', []).find((a) => a.value === assigneeId);
      if (!isUserAddedOrInvalid(task, formik, assigneeId)) {
        acceptTaskForExistingAssignee({ assigneeId, operatorIdArg, data, reqAssignee });
      } else {
        acceptTaskForNewAssignee({ assigneeId, operatorIdArg, data, reqAssignee });
      }
    } catch (err) {
      console.error(err);
      toastr.error(err);
    }
  };

  const rejectTask = async (assigneeId) => {
    try {
      const reqAssignee = _.get(assigneeOptions, 'items', []).find((a) => a.value === assigneeId);

      const data = { taskId: task.id, assigneeId };
      await ApiService.rejectTask(data);
      dispatch(acceptRejectTask(data, 'reject'));
      addComments({ dispatch, reqAssignee, loggedInUser, task, type: 'reject' });
      let assigneeIdsClone = _.cloneDeep(_.get(formik, 'values.s_assigneeid', []));
      assigneeIdsClone = assigneeIdsClone.filter((id) => id !== assigneeId);
      onAssigneeChange({ name: 's_assigneeid', value: assigneeIdsClone, formik });
      toastr.success('Task Rejected Sucessfully');
      history.goBack();
    } catch (err) {
      console.error(err);
      toastr.error(err);
    }
  };

  // is tasktype needs acceptance tasktype

  const allowAcceptOrReject = shouldAllowAcceptOrReject(task, formik, taskTypeOptions);

  const isAcceptRejectDisabled = useMemo(() => {
    if (!allowAcceptOrReject) {
      return true;
    }
    return determineIfUserHasFieldsDisabled({
      resource: RESOURCES.ENTITY.TASK,
      entityAccessType: ENTITY_ACCESS_TYPE.UPDATE,
      field: ACCESS_OPTIONS.DISABLED_FIELD.options.ACCEPTANCE_STATUS,
      user: loggedInUser,
    });
  }, [loggedInUser, formik, task]);

  const handleQrButton = useCallback(async ({ taskObj, assigneeId }) => {
    try {
      const taskDetails = {
        taskNumber: taskObj?.taskNumber ?? '',
        taskAsset: taskObj?.asset?.name ?? '',
        startTime: convertUTCDateStringToYYYYMMDDHHMMSSFormat(taskObj.startTime),
        taskName: taskObj?.taskType?.name,
        taskId: taskObj.id,
      };
      if (qrModalRef?.current?.setModalContent) {
        qrModalRef.current.setModalContent({ loading: true, open: true, taskDetails, header: 'Scan the qr code' });
      }
      const res = await ApiService.generateUrlForQr({ assigneeId, taskId: taskObj.id });

      if (qrModalRef?.current?.setModalContent) {
        qrModalRef.current.setModalContent({ loading: false, open: true, header: 'Scan the qr code', taskDetails });
      }
      if (qrModalRef?.current?.setQRvalue) {
        let qrValue = `${window.location.origin}/${res.urlPath}`;
        if (window.location.pathname.includes('joynfsm')) {
          qrValue = `${window.location.origin}/joynfsm/${res.urlPath}`;
        }
        qrModalRef.current.setQRvalue(qrValue);
      }
    } catch (error) {
      console.error('error', error.message);
      if (qrModalRef?.current?.setModalContent) {
        qrModalRef.current.setModalContent({ loading: false, open: false, header: 'Scan the qr code' });
      }
    }
  }, []);

  return (
    <>
      <div style={{ marginTop: '10px', width: `${task.id ? '100%' : '50%'}` }}>
        <label>ASSIGNEES</label>
        {!needEquipment ? (
          <Table id="partsTable">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={allowAcceptOrReject ? 5 : 3}>User</Table.HeaderCell>
                {allowAcceptOrReject && <Table.HeaderCell width={2}>Accept/Reject</Table.HeaderCell>}
                <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            {assigneeIds.map((id, index) => (
              <Table.Body key={index}>
                <Table.Row>
                  <Table.Cell>
                    <Form.Field style={{ height: '50px' }}>
                      <CustomDropdown
                        placeholder="Choose Assignees"
                        search
                        selection
                        options={filterUsedNames(formik, assigneeOptions, id)}
                        value={id}
                        onChange={(event, data) => {
                          handleFieldChange(data, index);
                        }}
                        name="assigneeid"
                        selectOnBlur={false}
                        clearable
                        disabled={task.isTaskLocked}
                      />
                    </Form.Field>
                  </Table.Cell>
                  {allowAcceptOrReject && (
                    <Table.Cell>
                      <Form.Field>
                        <div style={{ display: 'flex' }}>
                          <>
                            <Joyn2Button
                              size="medium"
                              label="Accept"
                              type="button"
                              btnType="primary"
                              onClick={index + 1 === assigneeIds.length ? null : () => acceptTask(id)}
                              disabled={
                                isAcceptRejectDisabled ||
                                hasAssigneeAccepted(task, id, selectedttOriginalObject) ||
                                index + 1 === assigneeIds.length
                              }
                            />
                            <Joyn2Button
                              size="medium"
                              type="button"
                              label="Reject"
                              btnType="secondary"
                              onClick={index + 1 === assigneeIds.length ? null : () => rejectTask(id)}
                              margin="0 12px 0 12px"
                              disabled={
                                task.isTaskLocked ||
                                isAcceptRejectDisabled ||
                                hasAssigneeAccepted(task, id, selectedttOriginalObject) ||
                                isUserAddedOrInvalid(task, formik, id) ||
                                index + 1 === assigneeIds.length
                              }
                            />
                          </>
                        </div>
                      </Form.Field>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <Form.Field>
                      <div style={{ display: 'flex' }}>
                        <div className="p-cursor" style={{ marginRight: '5px' }}>
                          {id ? (
                            <Icon
                              name="qrcode"
                              onClick={() => {
                                handleQrButton({ assigneeId: id, taskObj: task });
                              }}
                            />
                          ) : (
                            <Icon disabled name="qrcode" />
                          )}
                        </div>
                        <div
                          className="p-cursor"
                          style={{ marginRight: '5px' }}
                          onClick={id ? () => viewAssigneeDetals(id) : null}
                        >
                          {id ? <IButton /> : <IButtonDisabled />}
                        </div>

                        <div
                          onClick={index + 1 === assigneeIds.length ? null : () => handleDeleteRow(index)}
                          className="p-cursor"
                        >
                          <Icon
                            name="trash alternate outline"
                            disabled={task.isTaskLocked || index + 1 === assigneeIds.length}
                          />
                        </div>
                      </div>
                    </Form.Field>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            ))}
          </Table>
        ) : (
          <Table id="partsTable">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={3}>Equipment</Table.HeaderCell>
                <Table.HeaderCell width={3}>Operator</Table.HeaderCell>
                {allowAcceptOrReject && <Table.HeaderCell width={2}>Accept/Reject</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>
                  <Form.Field style={{ height: '50px', display: 'flex' }}>
                    <CustomDropdown
                      placeholder="Choose Equipment"
                      search
                      selection
                      options={{ items: equipmentOptions, isLoading: assigneeOptions.isLoading }}
                      value={equipmentId}
                      onChange={(event, data) => {
                        handleFieldChange(data, 0);
                      }}
                      name="equipmentId"
                      selectOnBlur={false}
                      clearable
                      disabled={task.isTaskLocked}
                    />
                    <div
                      className="p-cursor"
                      style={{ margin: '15px 5px 0 5px' }}
                      onClick={equipmentId ? () => viewAssigneeDetals(equipmentId) : null}
                    >
                      {equipmentId ? <IButton /> : <IButtonDisabled />}
                    </div>
                  </Form.Field>
                </Table.Cell>
                <Table.Cell>
                  <Form.Field style={{ height: '50px', display: 'flex' }}>
                    <CustomDropdown
                      placeholder="Choose Operator"
                      search
                      selection
                      options={{ items: operatorOptions, isLoading: assigneeOptions.isLoading }}
                      value={operatorId}
                      onChange={(event, data) => {
                        handleFieldChange(data, 1);
                      }}
                      name="operatorId"
                      selectOnBlur={false}
                      clearable
                      disabled={!equipmentId || task.isTaskLocked}
                    />
                    <div
                      className="p-cursor"
                      style={{ margin: '15px 5px 0 5px' }}
                      onClick={operatorId ? () => viewAssigneeDetals(operatorId) : null}
                    >
                      {operatorId ? <IButton /> : <IButtonDisabled />}
                    </div>
                    <div className="p-cursor" style={{ margin: '15px 5px 0 5px' }}>
                      {operatorId ? (
                        <Icon
                          name="qrcode"
                          onClick={() => {
                            handleQrButton({ assigneeId: operatorId, taskObj: task });
                          }}
                        />
                      ) : (
                        <Icon disabled name="qrcode" />
                      )}
                    </div>
                  </Form.Field>
                </Table.Cell>
                {allowAcceptOrReject && (
                  <Table.Cell>
                    <Form.Field>
                      <div style={{ display: 'flex' }}>
                        <>
                          <Joyn2Button
                            size="medium"
                            label="Accept"
                            type="button"
                            btnType="primary"
                            onClick={() => acceptTask(equipmentId, operatorId)}
                            disabled={
                              !equipmentId ||
                              isAcceptRejectDisabled ||
                              hasAssigneeAccepted(task, equipmentId, selectedttOriginalObject)
                            }
                          />
                          <Joyn2Button
                            size="medium"
                            type="button"
                            label="Reject"
                            btnType="secondary"
                            onClick={() => rejectTask(equipmentId)}
                            margin="0 12px 0 12px"
                            disabled={
                              !equipmentId ||
                              task.isTaskLocked ||
                              isAcceptRejectDisabled ||
                              hasAssigneeAccepted(task, equipmentId, selectedttOriginalObject) ||
                              isUserAddedOrInvalid(task, formik, operatorId)
                            }
                          />
                        </>
                      </div>
                    </Form.Field>
                  </Table.Cell>
                )}
              </Table.Row>
            </Table.Body>
          </Table>
        )}
      </div>
      <QRCodePopup ref={qrModalRef} />
    </>
  );
};

export default AssigneeComponent;
